/**
 * ProjectChatView — the real unified project chat UI. **Replaces D9's `UnifiedChatPanel` harness.**
 *
 * D9 shipped `UnifiedChatPanel.tsx` explicitly labelled "A TEST HARNESS, NOT FINISHED UI. PHASE F
 * REPLACES IT." This is that replacement — not a second chat component alongside it. `UnifiedChatPanel`
 * is deleted in the same change (Track 3 §1.3 deprecation map, 4 files).
 *
 * What is REUSED from D9 rather than rewritten: the entire data layer (`useProjectChat` —
 * `project_chat_sessions` / `_messages` / `_message_results`, the Phase E WebSocket stream key, the
 * single reconnect-recovery refetch, and the accept/reject PATCH path). D9's own comment said the
 * hook "is stable and should survive"; it has, untouched. Only the UI shell is new.
 *
 * ── TWO VARIANTS, ONE SESSION ──────────────────────────────────────────────────────────────────
 *   • `variant="full"`   — the page's default view when no file tab is open (CR §3.4).
 *   • `variant="docked"` — rendered inside `FileContentView`'s Edit mode, with the open file
 *     pre-tagged (CR Decision 1).
 *
 * The docked variant is **a filtered view onto the same session**, not a second conversation. That is
 * architectural, not stylistic: `project_chat_sessions` carries `UNIQUE (project_id)`, so a second
 * thread cannot exist; and two concurrent threads would invalidate each other's `base_content_hash`
 * continuously, marking every open proposal in the other thread stale on each accept (CR Decision 1,
 * reasons 3 and 4). Both variants therefore read and write the same cache keys and the same tables.
 *
 * ── AUTO-TAGGING ───────────────────────────────────────────────────────────────────────────────
 * `initialTags` pre-populates the composer so opening the chat from inside a file behaves like the
 * deleted per-file chat did. The tag stays **removable** — CR Decision 1 requires that explicitly,
 * because the whole point is that tagging is a superset of per-file chat rather than a constraint.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { cn } from "@/lib/utils";
import { toastIfNotProjectMember } from "@/lib/project-permissions";
import type { ArtifactType } from "@/hooks/useArtifacts";
import {
  useProjectChat,
  useProjectChatReconnectRecovery,
  useProjectChatStream,
  usePostProjectChatMessage,
} from "@/hooks/useProjectChat";
import { ChatComposer } from "./ChatComposer";
import { ChatTranscript } from "./ChatTranscript";
import {
  ChatSystemPrompt,
  type ChatSystemPromptGeneration,
} from "./ChatSystemPrompt";
import { FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ArtifactsState } from "@/hooks/useArtifacts";

export interface ProjectChatViewProps {
  projectId: string;
  variant?: "full" | "docked";
  /**
   * Files pre-tagged on mount. Set to `[artifactType]` by `FileContentView` when the chat is opened
   * from inside a file (CR Decision 1 auto-tagging).
   */
  initialTags?: ArtifactType[];
  /** Increment to move focus into the composer (tab close, proposal resolved). */
  focusToken?: number;
  /**
   * Opens a file tab. Supplied by `Phase0Page` so a locked-file skip notice can offer "Open
   * <file>" — the file view is where the Withdraw and Submit New Version controls live, i.e. the
   * only existing ways out of a lock. Omitted by the docked variant, which is already in a file.
   */
  onOpenArtifact?: (artifactType: ArtifactType) => void;

  // ── Item 7 (§8) — the Sources button moved into the composer's file-tag bar ─────────────────
  /**
   * Toggles the Sources panel. Rendered into the tag bar's trailing slot, and ONLY on
   * `variant="full"` — the docked variant is already inside a file, where a file picker is noise.
   */
  onOpenSources?: () => void;
  /** Input-document count, for the button's accessible name and its badge. */
  sourcesCount?: number;
  /** Whether the Sources panel is open — carries `aria-expanded` with the button it describes. */
  sourcesExpanded?: boolean;

  // ── Item 3 / AD-8 ───────────────────────────────────────────────────────────────────────────
  /**
   * Which artifacts exist, forwarded to `ArtifactTagBar`. `undefined` means "not yet known →
   * enable all five" and is distinct from `[]`, "none exist → disable all". The call site maps BOTH
   * unresolved `ArtifactsState` phases to `undefined` (§3.3.1).
   */
  availableArtifacts?: readonly ArtifactType[];

  // ── Item 2 (§3.2) — the generation prompt's inputs, forwarded to `ChatSystemPrompt` ─────────
  /**
   * Three-phase artifact existence. Omitted by the docked variant, which renders no prompt.
   * See `ChatSystemPromptProps`'s safety contract before changing the shape of this.
   */
  artifacts?: ArtifactsState;
  generation?: ChatSystemPromptGeneration | null;
  latestInputUploadedAt?: string | null;
  canManage?: boolean;
  isGenerating?: boolean;
  /** State B only — the BARE generate mutation. */
  onGenerate?: () => void;
  /** States C/D and the §3.2.2 line — `handleRegenerateClick`, preserving the ConflictDialog. */
  onRegenerate?: () => void;

  /**
   * Whether the viewer may POST turns and accept / reject proposals — a member of the project's
   * team, or leadership/admin (lib/project-permissions.ts). False leaves the transcript readable
   * with the composer disabled and Accept/Reject hidden; the server answers 403 NOT_PROJECT_MEMBER
   * otherwise. Defaults to true for standalone renders; both production callers pass the gate.
   */
  canWrite?: boolean;

  className?: string;
}

export function ProjectChatView({
  projectId,
  variant = "full",
  initialTags = [],
  focusToken = 0,
  onOpenArtifact,
  onOpenSources,
  sourcesCount = 0,
  sourcesExpanded = false,
  availableArtifacts,
  artifacts,
  generation = null,
  latestInputUploadedAt = null,
  canManage = false,
  isGenerating = false,
  onGenerate,
  onRegenerate,
  canWrite = true,
  className,
}: ProjectChatViewProps): JSX.Element {
  const [input, setInput] = useState<string>("");
  const [tags, setTags] = useState<ArtifactType[]>(initialTags);
  // Internal focus requests (proposal resolved) are added to the parent's token so both sources can
  // move focus without fighting over one piece of state.
  const [internalFocusBumps, setInternalFocusBumps] = useState(0);

  const { data, isLoading } = useProjectChat(projectId);
  // E1: live stage/narration, read from the socket-written cache key.
  const stream = useProjectChatStream(projectId);
  // E7: the single reconnect-recovery refetch that replaced the 5-second poll. It is an event
  // listener, not a timer. The full-page and docked variants CAN be mounted at once (Phase0Page
  // keeps the full-page chat mounted-but-hidden while a file tab is active), so `ws-online` fires
  // two invalidations of the same key — React Query collapses those into one fetch, and neither is
  // periodic, so E7's "recovers by event, never by interval" property holds.
  useProjectChatReconnectRecovery(projectId);
  const { mutateAsync: postMessage, isPending: isSending } =
    usePostProjectChatMessage(projectId);

  // Re-apply the auto-tag if the hosting file changes without this component remounting. Today
  // `FileContentView` unmounts per tab so this is defensive rather than load-bearing; it is guarded
  // on the auto-tag set changing so a user who deliberately removed a chip does not have it forced
  // back on every render.
  const autoTagKey = initialTags.join("|");
  const previousAutoTagKey = useRef<string>(autoTagKey);
  useEffect(() => {
    if (autoTagKey === previousAutoTagKey.current) return;
    previousAutoTagKey.current = autoTagKey;
    if (initialTags.length === 0) return;
    setTags((prev) => {
      const merged = new Set([...prev, ...initialTags]);
      return Array.from(merged);
    });
    // `initialTags` is covered by `autoTagKey`, which is its stable serialisation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoTagKey]);

  const messages = useMemo(() => data?.messages ?? [], [data]);
  const isTurnInFlight = messages.some(
    (m) => m.status === "pending" || m.status === "processing",
  );
  const isBusy = isSending || isTurnInFlight;

  const toggleTag = useCallback((artifactType: ArtifactType): void => {
    setTags((prev) =>
      prev.includes(artifactType)
        ? prev.filter((t) => t !== artifactType)
        : [...prev, artifactType],
    );
  }, []);

  const handleSend = useCallback(async (): Promise<void> => {
    const message = input.trim();
    if (message.length === 0 || isBusy) return;
    try {
      await postMessage({ message, taggedArtifactTypes: tags });
      setInput("");
      // Tags are deliberately NOT cleared — see ChatComposer's docstring.
    } catch (err) {
      if (toastIfNotProjectMember(err)) return;
      const serverMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not send the message.";
      toast.error(serverMessage);
    }
  }, [input, isBusy, postMessage, tags]);

  // Track 3 T3-4: resolving a proposal unmounts the focused card, so focus is moved to the composer
  // instead of being dropped on <body>.
  const handleResultResolved = useCallback((): void => {
    setInternalFocusBumps((n) => n + 1);
  }, []);

  const isDocked = variant === "docked";

  return (
    <section
      aria-label={isDocked ? "Project chat for this file" : "Project chat"}
      className={cn(
        "flex min-h-0 flex-col bg-white",
        // ⚠️ `flex-1` is LOAD-BEARING on the full variant — do not drop it back to `h-full` alone.
        //
        // RE-ANCHORED 2026-10-03 (P0 fix batch, Item 6 / C3). The parent's height changed: Item 6
        // replaced the chat tabpanel's `min-h-[620px]` column with a MEASURED inline pixel height
        // (`Phase0Page`'s `workspaceHeight`) and a `min-h-[420px]` floor. So the parent now has a
        // DEFINITE height, and `h-full` alone would in fact resolve today.
        //
        // `flex-1` is RETAINED anyway, deliberately. The measurement is one `useLayoutEffect` away
        // from being removed or from failing to run (an SSR pass, a ref that never attaches, a
        // future refactor that drops the inline style), and the failure mode is severe and silent
        // — see the measurements below. `flex-1` sizes this section by flex distribution rather than
        // by percentage, which works against BOTH a definite and a flex-grown parent. `h-full` is
        // kept only so the component still fills a parent that has a real definite height.
        //
        // ── EVIDENCE, measured against the PRE-2026-10-03 `min-h-[620px]` layout ───────────────
        // These numbers no longer reproduce on the current layout, and are retained because they
        // are the evidence for the rule rather than a description of today's DOM. Back then the
        // parent's height came from `flex-1` inside `min-h-[620px]`; a height produced by flex-grow
        // is INDEFINITE for percentage resolution, so `height: 100%` silently computed to `auto`
        // and this section collapsed to its CONTENT height — 179px instead of 574px. The composer
        // has an intrinsic ~127px (chips + 44px textarea + hint), so it took 71% of the box and the
        // transcript was left with 52px: the reported "input takes ~70% of the chat box" bug.
        // With `flex-1`: section 574px, transcript 447px, composer 22%.
        // `h-full` is kept only so the component still fills a parent that has a real definite height.
        isDocked
          ? "h-[480px] rounded-lg border border-outlines"
          : "h-full flex-1",
        className,
      )}
    >
      {isDocked && (
        <div className="shrink-0 border-b border-outlines px-4 py-2">
          <p className="text-sm font-medium text-text-primary">Project chat</p>
          <p className="text-xs text-text-secondary">
            One conversation for the whole project — this file is tagged for
            you. Untag it and tag others to work on different files.
          </p>
        </div>
      )}

      <ChatTranscript
        projectId={projectId}
        messages={messages}
        stream={stream}
        isLoading={isLoading}
        onResultResolved={handleResultResolved}
        onOpenArtifact={onOpenArtifact}
        canResolve={canWrite}
        /**
         * Item 2 — the generation prompt renders ABOVE the message list but INSIDE the transcript's
         * scroll container, so it scrolls away with history rather than pinning to the top forever.
         *
         * Passed as a node rather than as data so `ChatTranscript` stays a pure transcript renderer
         * and does not take on the eight generation-state props. It is only built for the full
         * variant; `ChatSystemPrompt` also returns null for `variant="docked"`, so the guard is
         * belt-and-braces and the component stays correct if ever rendered directly.
         */
        systemPrompt={
          !isDocked && artifacts ? (
            <ChatSystemPrompt
              artifacts={artifacts}
              sourceCount={sourcesCount}
              generation={generation}
              latestInputUploadedAt={latestInputUploadedAt}
              canManage={canManage}
              isGenerating={isGenerating}
              variant={variant}
              onGenerate={onGenerate ?? (() => undefined)}
              onRegenerate={onRegenerate ?? (() => undefined)}
              onOpenSources={onOpenSources ?? (() => undefined)}
            />
          ) : null
        }
      />

      <ChatComposer
        value={input}
        onChange={setInput}
        onSend={() => void handleSend()}
        tags={tags}
        onToggleTag={toggleTag}
        autoTagged={initialTags}
        available={availableArtifacts}
        isBusy={isBusy}
        readOnly={!canWrite}
        focusToken={focusToken + internalFocusBumps}
        /**
         * Item 7 (§8) — the Sources button, moved out of `Phase0Page`'s header into the file-tag
         * bar. Full variant only: the docked variant is already inside a file.
         *
         * Two accessibility fixes travel with the move:
         *   • The count badge gets an accessible NAME. Previously a screen reader heard
         *     "Sources 2" with no unit.
         *   • `aria-expanded` moves WITH the button, so it still describes the panel it controls.
         */
        trailing={
          !isDocked && onOpenSources ? (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto shrink-0"
              onClick={onOpenSources}
              aria-expanded={sourcesExpanded}
              aria-label={`Sources — ${sourcesCount} input document${sourcesCount === 1 ? "" : "s"}`}
            >
              <FolderOpen className="h-4 w-4" aria-hidden="true" />
              Sources
              {sourcesCount > 0 && (
                <span className="ml-1 text-xs text-text-secondary">
                  {sourcesCount}
                </span>
              )}
            </Button>
          ) : null
        }
      />
    </section>
  );
}

export default ProjectChatView;
